"use client";

import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import { IoSearchSharp } from "react-icons/io5";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import { useNavigate } from "react-router-dom";
import heroImg from "../assets/images/heroimg.png";
import CreateCompanyFooter from "../components/createcompanyfooter/CreateCompanyFooter";

// Import company data
import companyData from "../assets/response.json";

// Validation schema using Zod
const addManagerSchema = z.object({
  firstName: z
    .string()
    .min(2, "First Name must be at least 2 characters")
    .max(25, "First Name must be less than 25 characters"),
  lastName: z
    .string()
    .min(2, "Last Name must be at least 2 characters")
    .max(25, "Last Name must be less than 25 characters"),
  managementPosition: z.string().min(1, "Management Position is required"),
  company: z
    .string()
    .min(2, "Company name must be at least 2 characters")
    .max(50, "Company Name must be less than 50 characters"),
  linkedin_profile: z.string().optional(),
  cityState: z.string().min(3, "City, State must be at least 3 characters"),
});

const techCities = [
  "New York, NY",
  "Redwood City, CA",
  "Los Angeles, CA",
  "Chicago, IL",
  "Houston, TX",
  "Phoenix, AZ",
  "Philadelphia, PA",
  "San Antonio, TX",
  "San Diego, CA",
  "Dallas, TX",
  "Jacksonville, FL",
  "Austin, TX",
  "Fort Worth, TX",
  "San Jose, CA",
  "Columbus, OH",
  "Charlotte, NC",
  "Indianapolis, IN",
  "San Francisco, CA",
  "Seattle, WA",
  "Denver, CO",
  "Oklahoma City, OK",
  "Nashville, TN",
  "Washington, DC",
  "El Paso, TX",
  "Las Vegas, NV",
  "Boston, MA",
  "Detroit, MI",
  "Portland, OR",
  "Louisville, KY",
  "Memphis, TN",
  "Baltimore, MD",
  "Milwaukee, WI",
  "Albuquerque, NM",
  "Tucson, AZ",
  "Fresno, CA",
  "Sacramento, CA",
  "Mesa, AZ",
  "Atlanta, GA",
  "Kansas City, MO",
  "Colorado Springs, CO",
  "Omaha, NE",
  "Raleigh, NC",
  "Miami, FL",
  "Virginia Beach, VA",
  "Long Beach, CA",
  "Oakland, CA",
  "Minneapolis, MN",
  "Bakersfield, CA",
  "Tulsa, OK",
  "Tampa, FL",
  "Arlington, TX",
  "Wichita, KS",
  "Aurora, CO",
  "New Orleans, LA",
  "Cleveland, OH",
  "Honolulu, HI",
  "City, State",
  "Anaheim, CA",
  "Henderson, NV",
  "Orlando, FL",
  "Lexington, KY",
  "Stockton, CA",
  "Riverside, CA",
  "Corpus Christi, TX",
  "Irvine, CA",
  "Cincinnati, OH",
  "Santa Ana, CA",
  "Newark, NJ",
  "Saint Paul, MN",
  "Pittsburgh, PA",
  "Greensboro, NC",
  "Durham, NC",
  "Lincoln, NE",
  "Jersey City, NJ",
  "Plano, TX",
  "Tokyo, Japan",
  "Delhi, India",
  "Shanghai, China",
  "Dhaka, Bangladesh",
  "Cairo, Egypt",
  "Sao Paulo, Brazil",
  "Mexico City, Mexico",
  "Beijing, China",
  "Mumbai, India",
  "Osaka, Japan",
  "Chongqing, China",
  "Karachi, Pakistan",
  "Kinshasa, DR Congo",
  "Lagos, Nigeria",
  "Istanbul, Turkey",
  "Kolkata, India",
  "Buenos Aires, Argentina",
  "Manila, Philippines",
  "Guangzhou, China",
  "Lahore, Pakistan",
  "Tianjin, China",
  "Bangalore, India",
  "Rio de Janeiro, Brazil",
  "Shenzhen, China",
  "Moscow, Russia",
  "Chennai, India",
  "Bogota, Colombia",
  "Jakarta, Indonesia",
  "Lima, Peru",
  "Bangkok, Thailand",
  "Paris, France",
  "Hyderabad, India",
  "Nanjing, China",
  "Luanda, Angola",
  "Seoul, South Korea",
  "Chengdu, China",
  "London, United Kingdom",
  "Ho Chi Minh City, Vietnam",
  "Tehran, Iran",
  "Nagoya, Japan",
  "Xi-an, China",
  "Ahmedabad, India",
  "Kuala Lumpur, Malaysia",
  "Wuhan, China",
  "Suzhou, China",
  "Hangzhou, China",
  "Surat, India",
  "Dar es Salaam, Tanzania",
  "Baghdad, Iraq",
  "Shenyang, China",
  "Riyadh, Saudi Arabia",
  "Foshan, China",
  "Dongguan, China",
  "Hong Kong, Hong Kong",
  "Pune, India",
  "Haerbin, China",
  "Santiago, Chile",
  "Madrid, Spain",
  "Khartoum, Sudan",
  "Toronto, Canada",
  "Johannesburg, South Africa",
  "Belo Horizonte, Brazil",
  "Dalian, China",
  "Qingdao, China",
  "Singapore, Singapore",
  "Zhengzhou, China",
  "Ji nan Shandong, China",
  "Abidjan, Ivory Coast",
  "Addis Ababa, Ethiopia",
  "Yangon, Myanmar",
  "Alexandria, Egypt",
  "Nairobi, Kenya",
  "Barcelona, Spain",
  "Chittagong, Bangladesh",
  "Hanoi, Vietnam",
  "Saint Petersburg, Russia",
  "Guadalajara, Mexico",
  "Ankara, Turkey",
  "Fukuoka, Japan",
  "Melbourne, Australia",
  "Monterrey, Mexico",
  "Sydney, Australia",
  "Urumqi, China",
  "Changsha, China",
  "Cape Town, South Africa",
  "Jiddah, Saudi Arabia",
  "Brasilia, Brazil",
  "Kunming, China",
  "Changchun, China",
  "Kabul, Afghanistan",
  "Yaounde, Cameroon",
  "Hefei, China",
  "Ningbo, China",
  "Shantou, China",
  "Kano, Nigeria",
  "Tel Aviv, Israel",
  "Shijiazhuang, China",
  "Jaipur, India",
  "Kozhikode, India",
  "Nanning, China",
  "Montreal, Canada",
  "Rome, Italy",
  "Recife, Brazil",
  "Malappuram, India",
  "Taiyuan Shanxi, China",
  "Fortaleza, Brazil",
  "Porto Alegre, Brazil",
  "Ekurhuleni, South Africa",
  "Abuja, Nigeria",
  "Changzhou, China",
  "Ibadan, Nigeria",
  "Lucknow, India",
  "Nanchang, China",
  "Wenzhou, China",
  "Xiamen, China",
  "Fuzhou Fujian, China",
  "Kumasi, Ghana",
  "Salvador, Brazil",
  "Tangshan Hebei, China",
  "Casablanca, Morocco",
  "Bekasi, Indonesia",
  "Faisalabad, Pakistan",
  "Curitiba, Brazil",
  "Port Harcourt, Nigeria",
  "Brisbane, Australia",
  "Peshawar, Pakistan",
  "Gujranwala, Pakistan",
  "Medan, Indonesia",
  "Hohhot, China",
  "Baku, Azerbaijan",
  "Rawalpindi, Pakistan",
  "Agra, India",
  "Kannur, India",
  "Belem, Brazil",
  "Liuzhou, China",
  "Visakhapatnam, India",
  "Aleppo, Syria",
  "Manaus, Brazil",
  "San Juan, Puerto Rico",
  "Maracaibo, Venezuela",
  "Phnom Penh, Cambodia",
  "Baotou, China",
  "Vadodara, India",
  "Barranquilla, Colombia",
  "Beirut, Lebanon",
  "Xuzhou, China",
  "Taoyuan, Taiwan",
  "Nashik, India",
  "Vijayawada, India",
  "Sendai, Japan",
  "Tijuana, Mexico",
  "Esfahan, Iran",
  "Putian, China",
  "Amman, Jordan",
  "Multan, Pakistan",
  "Wuhu Anhui, China",
  "Kollam, India",
  "Conakry, Guinea",
  "Grande Vitoria, Brazil",
  "Mecca, Saudi Arabia",
  "Yangzhou, China",
  "Taizhou Zhejiang, China",
  "Baoding, China",
  "Naples, Italy",
  "Daegu, South Korea",
  "Perth, Australia",
  "Linyi Shandong, China",
  "Rajkot, India",
  "Brussels, Belgium",
  "Haikou, China",
  "Daqing, China",
  "Hyderabad, Pakistan",
  "Semarang, Indonesia",
  "Ludhiana, India",
  "Vienna, Austria",
  "La Paz, Bolivia",
  "Baixada Santista, Brazil",
  "Zhuhai, China",
  "Quanzhou, China",
  "West Yorkshire, United Kingdom",
  "Raipur, India",
  "Madurai, India",
  "Sharjah, United Arab Emirates",
  "Santa Cruz, Bolivia",
  "Meerut, India",
  "Jiangmen, China",
  "Varanasi, India",
  "Turin, Italy",
  "Xiangyang, China",
  "Yichang, China",
  "Warsaw, Poland",
  "Yinchuan, China",
  "Monrovia, Liberia",
  "Montevideo, Uruguay",
  "Hamburg, Germany",
  "Lyon, France",
  "Tiruppur, India",
  "Budapest, Hungary",
  "Srinagar, India",
  "Jamshedpur, India",
  "Aurangabad, India",
  "Stockholm, Sweden",
  "Makassar, Indonesia",
  "Glasgow, United Kingdom",
  "Auckland, New Zealand",
];

const managementPositions = [
  "AI Engineering Manager",
  "AI Manager",
  "Applied Science Manager",
  "Assistant Vice President",
  "Assistant to Vice President",
  "Corporate Vice President",
  "Data Engineering Manager",
  "Director",
  "Director of Engineering",
  "Director of Software Development",
  "Engineering Manager",
  "Engineering Manager II",
  "Engineering Support Manager",
  "Hardware Engineering Manager",
  "Manager",
  "Manager, Data Engineering",
  "ML Engineering Manager",
  "ML Engineering Manager II",
  "Principal Engineering Manager",
  "Principal Software Eng Manager",
  "Principal Software Engineering Manager",
  "Principal Software Development Manager",
  "QA Engineering Manager",
  "QA Manager",
  "Quality Assurance Manager",
  "Quality Manager",
  "Reliability Maintenance Engineering Area Manager",
  "Security Engineering Manager",
  "Senior Applied Science Manager",
  "Senior Director of Engineering",
  "Senior Engineering Manager",
  "Senior Manager, Applied Science",
  "Senior Manager, Software Development",
  "Senior ML Engineering Manager",
  "Senior QA Engineering Manager",
  "Senior Software Development Manager",
  "Senior Software Engineering Manager",
  "Senior Vice President",
  "Site Reliability Engineering Manager",
  "Software Development Manager",
  "Software Development Manager II",
  "Software Development Manager III",
  "Software Engineering Manager",
  "Software Engineering Manager II",
  "Sr Applied Science Manager",
  "Sr Director of Engineering",
  "Sr Engineering Manager",
  "Sr Software Development Manager",
  "Sr Software Engineering Manager",
  "Sr Vice President",
  "Sr. Manager, Software Development",
  "Staff Engineering Manager",
  "Support Engineering Manager",
  "System Engineering Manager",
  "VP",
  "VP of Engineering",
  "Vice President",
  "Vice President - Engineering Manager",
  "Vice President - Executive",
  "Vice President - Sr Engineering Manager",
  "Vice President of Engineering"
];

const AddManager = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newManager, setNewManager] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState(companyData);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // API error surfaced near submit button
  const [apiError, setApiError] = useState(null);
  const errorRef = useRef(null);

  const dropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const router = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(addManagerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      managementPosition: "",
      company: "",
      linkedin_profile: "",
      cityState: "",
    },
  });

  const watchedValues = watch();

  const [linkedInSlug, setLinkedInSlug] = useState("");

  const handleLinkedInChange = (input) => {
    try {
      let slug = input.trim();
      const match = slug.match(/linkedin\.com\/in\/([^/?#]+)/i);
      if (match) slug = match[1];
      slug = slug.split("?")[0].split("#")[0];
      setLinkedInSlug(slug);
      setValue("linkedin_profile", slug);
    } catch {
      setLinkedInSlug("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        if (!watchedValues.managementPosition) setFocusedField(null);
      }
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target)
      ) {
        handleCompanyDropdownClose();
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setCityDropdownOpen(false);
        if (!watchedValues.cityState) setFocusedField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.managementPosition,
    watchedValues.company,
    watchedValues.cityState,
  ]);

  // Accepts: linkedin.com/in/<id> with optional http/https and subdomain
  const LINKEDIN_IN_RE =
    /^(?:(?:https?:)?\/\/)?(?:[\w-]+\.)?linkedin\.com\/in\/[A-Za-z0-9._-]{3,100}\/?$/i;

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setCitySearchResults(techCities);
    } else {
      const filtered = techCities.filter((city) =>
        city.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setCitySearchResults(filtered);
    }
  }, [searchQuery]);

  // Company search functionality
  const handleCompanySearch = (query) => {
    setCompanySearchQuery(query);
    if (query.trim() === "") {
      setFilteredCompanies(companyData);
    } else {
      const filtered = companyData.filter((company) =>
        company.company_name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCompanies(filtered);
    }
  };

  const handleCompanySearchKeyDown = (e) => {
    if (e.key === "Escape") handleCompanyDropdownClose();
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setValue("company", company.company_name);
    setCompanyDropdownOpen(false);
    setFocusedField("company");
    setCompanySearchQuery("");
    setFilteredCompanies(companyData);
  };

  const clearCompanySelection = () => {
    setSelectedCompany(null);
    setCompanySearchQuery("");
    setFilteredCompanies(companyData);
  };

  const handleCompanyDropdownClose = () => {
    setCompanyDropdownOpen(false);
    if (!watchedValues.company) setFocusedField(null);
    setCompanySearchQuery("");
    setFilteredCompanies(companyData);
  };

  // Submit with server error surfacing near the submit button
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setApiError(null);

    try {
      const apiUrl = `${getApiBaseUrl()}/manager_creation`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: data.firstName,
          last_name: data.lastName,
          position: data.managementPosition,
          company_name: data.company,
          location: data.cityState,
          linkedin_profile: data.linkedin_profile || "",
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      } else {
        const text = await response.text();
        try {
          payload = JSON.parse(text);
        } catch {
          payload = { raw: text };
        }
      }

      if (!response.ok) {
        const message =
          payload?.error?.message ||
          payload?.message ||
          (typeof payload === "string" ? payload : null) ||
          `Request failed (${response.status})`;
        setApiError(message);
        // bring the error into view by the submit button
        setTimeout(() => {
          if (errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 0);
        return;
      }

      const managerProfile = payload?.manager_profile;
      if (managerProfile) {
        setNewManager(managerProfile);
        setShowSuccessModal(true);
        reset();
        clearCompanySelection();
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to submit manager";
      setApiError(message);
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirectToProfile = () => {
    if (newManager) {
      const fullName = encodeURIComponent(newManager.manager_full_name);
      router(`/manager?name=${fullName}&id=${newManager.manager_id}`);
    }
  };

  const isFieldActive = (fieldName) => {
    const value = watchedValues[fieldName];
    const isFocused = focusedField === fieldName;
    const isDropdownOpen =
      (fieldName === "managementPosition" && dropdownOpen) ||
      (fieldName === "company" && companyDropdownOpen) ||
      (fieldName === "cityState" && cityDropdownOpen);
    return (value && value.trim() !== "") || isFocused || isDropdownOpen;
  };

  return (
    <div className="page-container">
      {/* Inline style just for the API error alert */}
      <style>{`
        .api-error-alert{
          background:#fff3f2;
          border:1px solid #f5c2c7;
          color:#842029;
          padding:12px 14px;
          border-radius:8px;
          margin-top:12px;
          font-weight:600;
        }
      `}</style>

      <div className="main-content">
        {/* Hero Section */}
        <div className="">
          <div className="row">
            <div className="col-12">
              <div className="">
                <div className="d-flex justify-content-center">
                  <img
                    src={heroImg}
                    className="img-fluid"
                    style={{ width: "250px" }}
                    alt="ICON"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="add_company_h3 pt-3 pb-3">Add Your Manager</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="form-container">
          <form onSubmit={handleSubmit(onSubmit)} className="form">
            {/* First Name */}
            <div className="form-group">
              <input
                {...register("firstName")}
                type="text"
                id="firstName"
                className={`form-input dropdown-toggle ${
                  errors.firstName ? "input-error" : ""
                } ${isFieldActive("firstName") ? "input-active" : ""} label-medium`}
                placeholder=" "
                onFocus={() => setFocusedField("firstName")}
                onBlur={() => setFocusedField(null)}
              />
              <label
                htmlFor="firstName"
                className={`form-label ${
                  isFieldActive("firstName") ? "label-active" : ""
                }`}
              >
                First Name
              </label>
              {errors.firstName && (
                <div className="error-message">{errors.firstName.message}</div>
              )}
            </div>

            {/* Last Name */}
            <div className="form-group">
              <input
                {...register("lastName")}
                type="text"
                id="lastName"
                className={`form-input dropdown-toggle ${
                  errors.lastName ? "input-error" : ""
                } ${isFieldActive("lastName") ? "input-active" : ""} label-medium`}
                placeholder=" "
                onFocus={() => setFocusedField("lastName")}
                onBlur={() => setFocusedField(null)}
              />
              <label
                htmlFor="lastName"
                className={`form-label ${
                  isFieldActive("lastName") ? "label-active" : ""
                }`}
              >
                Last Name
              </label>
              {errors.lastName && (
                <div className="error-message">{errors.lastName.message}</div>
              )}
            </div>

            {/* Management Position */}
            <div
              className={`form-group ${dropdownOpen ? "z-30" : "z-10"}`}
              ref={dropdownRef}
            >
              <div
                className={`form-input dropdown-toggle ${
                  errors.managementPosition
                    ? "input-error"
                    : focusedField === "managementPosition" || dropdownOpen
                    ? "input-active"
                    : ""
                } ${
                  isFieldActive("managementPosition") ? "" : "input-placeholder"
                } label-medium`}
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setFocusedField("managementPosition");
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFocusedField("managementPosition");
                }}
                tabIndex={0}
              >
                <div className="dropdown-content">
                  <span
                    className={
                      isFieldActive("managementPosition")
                        ? "text-active"
                        : "text-placeholder"
                    }
                  >
                    {watchedValues.managementPosition}
                  </span>
                  {dropdownOpen ? (
                    <LuChevronUp className="dropdown-icon" />
                  ) : (
                    <LuChevronDown className="dropdown-icon" />
                  )}
                </div>
              </div>
              <label
                className={`form-label dropdown-toggle ${
                  isFieldActive("managementPosition") ? "label-active" : ""
                }`}
              >
                Management Position
              </label>

              {dropdownOpen && (
                <div className="dropdown-menu-add-manager">
                  {managementPositions.map((position, index) => (
                    <div
                      key={index}
                      className="dropdown-item"
                      onClick={() => {
                        setValue("managementPosition", position);
                        setDropdownOpen(false);
                        setFocusedField("managementPosition");
                      }}
                      style={{ fontWeight: "bold", fontSize: "12px" }}
                    >
                      {position}
                    </div>
                  ))}
                </div>
              )}
              {errors.managementPosition && (
                <div className="error-message">
                  {errors.managementPosition.message}
                </div>
              )}
            </div>

            {/* Company Select */}
            <div
              className={`form-group ${companyDropdownOpen ? "z-20" : "z-10"}`}
              ref={companyDropdownRef}
            >
              <div
                className={`form-input dropdown-toggle ${
                  errors.company
                    ? "input-error"
                    : focusedField === "company" || companyDropdownOpen
                    ? "input-active"
                    : ""
                } ${isFieldActive("company") ? "" : "input-placeholder"} label-medium`}
                onClick={() => {
                  if (companyDropdownOpen) {
                    handleCompanyDropdownClose();
                  } else {
                    setCompanyDropdownOpen(true);
                    setFocusedField("company");
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFocusedField("company");
                }}
                tabIndex={0}
              >
                <div className="dropdown-content">
                  {selectedCompany ? (
                    <div className="selected-company">
                      <img
                        src={selectedCompany.company_logo_url}
                        alt={selectedCompany.company_name}
                        className="selected-company-logo"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <span className="selected-company-name">
                        {selectedCompany.company_name}
                      </span>
                    </div>
                  ) : (
                    <span
                      className={
                        isFieldActive("company") ? "text-active" : "text-placeholder"
                      }
                    >
                      {watchedValues.company}
                    </span>
                  )}
                  {companyDropdownOpen ? (
                    <LuChevronUp className="dropdown-icon" />
                  ) : (
                    <LuChevronDown className="dropdown-icon" />
                  )}
                </div>
              </div>

              <label
                className={`form-label label-medium ${
                  isFieldActive("company") ? "label-active" : ""
                }`}
              >
                Company
              </label>

              {companyDropdownOpen && (
                <div className="dropdown-menu-add-manager">
                  {/* Company Search Input */}
                  <div className="company-search-container">
                    <div className="search-input-wrapper">
                      <input
                        type="text"
                        placeholder="Search companies..."
                        value={companySearchQuery}
                        onChange={(e) => handleCompanySearch(e.target.value)}
                        onKeyDown={handleCompanySearchKeyDown}
                        className="company-search-input has-right-icon"
                        autoFocus
                      />
                      <IoSearchSharp className="search-icon-right" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="dropdown-options">
                    {filteredCompanies.length > 0 ? (
                      filteredCompanies.map((company) => (
                        <div
                          key={company.company_id}
                          className="dropdown-item company-dropdown-item"
                          onClick={() => {
                            handleCompanySelect(company);
                          }}
                        >
                          <div className="company-item">
                            <img
                              src={company.company_logo_url}
                              alt={company.company_name}
                              className="company-logo"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <div className="company-info">
                              <div className="company-name">{company.company_name}</div>
                              {/* Optional meta */}
                              {/* <div className="company-meta">
                                  <div className="company-industry">{company.industry}</div>
                                  <div className="company-location">{company.location}</div>
                                </div> */}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-results">No companies found</div>
                    )}
                  </div>
                </div>
              )}

              {errors.company && (
                <div className="error-message">{errors.company.message}</div>
              )}
            </div>

            {/* LinkedIn Profile */}
            <div className="form-group">
              <input
                {...register("linkedin_profile", {
                  setValueAs: (v) => (v ? String(v).trim() : ""),
                  validate: (v) =>
                    !v || LINKEDIN_IN_RE.test(v)
                      ? true
                      : "Enter a LinkedIn profile like linkedin.com/in/your-id",
                })}
                type="text"
                id="linkedin_profile"
                className={`form-input dropdown-toggle ${
                  errors.linkedin_profile ? "input-error" : ""
                } ${isFieldActive("linkedin_profile") ? "input-active" : ""} label-medium`}
                placeholder=" "
                onFocus={() => setFocusedField("linkedin_profile")}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleLinkedInChange(e.target.value)}
              />
              <label
                htmlFor="linkedin_profile"
                className={`form-label ${isFieldActive("linkedin_profile") ? "label-active" : ""}`}
              >
                LinkedIn Profile (Optional)
              </label>
              {errors.linkedin_profile && (
                <div className="error-message">{errors.linkedin_profile.message}</div>
              )}
            </div>

            {/* City/State */}
            <div
              className={`form-group ${cityDropdownOpen ? "z-20" : "z-10"}`}
              ref={cityDropdownRef}
            >
              <input
                {...register("cityState")}
                type="text"
                id="cityState"
                className={`form-input dropdown-toggle ${
                  errors.cityState ? "input-error" : ""
                } ${isFieldActive("cityState") ? "input-active" : ""} label-medium`}
                placeholder=" "
                onChange={(e) => {
                  setValue("cityState", e.target.value);
                  setSearchQuery(e.target.value);
                  setCityDropdownOpen(true);
                }}
                onFocus={() => {
                  setFocusedField("cityState");
                  setCityDropdownOpen(true);
                }}
                onBlur={() => {
                  // handled by click outside
                }}
                value={watchedValues.cityState || ""}
              />
              <label
                htmlFor="cityState"
                className={`form-label ${
                  isFieldActive("cityState") ? "label-active" : ""
                } label-medium`}
              >
                City, State
              </label>

              <button
                type="button"
                className="dropdown-button"
                onClick={() => {
                  setCityDropdownOpen(!cityDropdownOpen);
                  setFocusedField("cityState");
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFocusedField("cityState");
                }}
              >
                {cityDropdownOpen ? (
                  <LuChevronUp className="dropdown-icon" />
                ) : (
                  <LuChevronDown className="dropdown-icon" />
                )}
              </button>

              {cityDropdownOpen && (
                <div className="dropdown-menu-add-manager">
                  {citySearchResults.map((city, index) => (
                    <div
                      key={index}
                      className="dropdown-item"
                      onClick={() => {
                        setValue("cityState", city);
                        setCityDropdownOpen(false);
                        setFocusedField("cityState");
                      }}
                    >
                      {city}
                    </div>
                  ))}
                </div>
              )}

              {errors.cityState && (
                <div className="error-message">{errors.cityState.message}</div>
              )}
            </div>

            {/* Submit Button */}
            <div className="submit-container">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`submit-button ${isSubmitting ? "button-disabled" : ""}`}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
            {/* API error shown near the submit button */}
            {apiError && (
              <div ref={errorRef} className="api-error-alert" role="alert">
                {apiError}
              </div>
            )}
          </form>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay">
            <div className="modal-content-add-manager">
              <div className="modal-body">
                <div className="success-icon-container">
                  <svg
                    className="success-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="modal-title">Manager Added Successfully!</h3>
                <p className="modal-description">
                  🎉 The manager has been added successfully. You can now view
                  their profile for more details.
                </p>
              </div>
              <div className="modal-actions">
                <button
                  onClick={handleRedirectToProfile}
                  className="modal-button modal-button-primary-add-manager"
                >
                  View Manager Profile
                </button>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="modal-button modal-button-secondary-add-manager"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <hr className="divider" />

      <CreateCompanyFooter />
    </div>
  );
};

export default AddManager;